import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import { getUserWorkouts, deleteWorkout } from '../services/firestore';

const History = () => {
    const { user } = useAuth();
    const { darkMode } = useTheme();
    const [workouts, setWorkouts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState(null); // Track which workout is being deleted

    useEffect(() => {
        const fetchWorkouts = async () => {
            if (user) {
                try {
                    const userWorkouts = await getUserWorkouts(user.uid);
                    setWorkouts(userWorkouts);
                } catch (error) {
                    console.error('Error fetching workouts:', error);
                    toast.error('Failed to load workout history');
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchWorkouts();
    }, [user]);

    const handleDeleteWorkout = async (workoutId) => {
        setDeleting(workoutId);

        try {
            await deleteWorkout(workoutId, user.uid);

            // Remove the workout from the local state
            setWorkouts(workouts.filter(workout => workout.id !== workoutId));

            toast.success('Workout deleted successfully! 🗑️');
        } catch (error) {
            console.error('Error deleting workout:', error);
            toast.error(`Failed to delete workout: ${error.message}`);
        } finally {
            setDeleting(null);
        }
    };

    const confirmDeleteWorkout = (workoutId, workoutDate) => {
        const workoutDateFormatted = new Date(workoutDate).toLocaleDateString();

        // Custom toast with confirmation buttons
        toast(
            ({ closeToast }) => (
                <div className="p-4">
                    <div className="mb-4">
                        <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`}>Delete Workout</h3>
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            Are you sure you want to delete the workout from {workoutDateFormatted}?
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={() => {
                                handleDeleteWorkout(workoutId);
                                closeToast();
                            }}
                            disabled={deleting === workoutId}
                            className="flex-1 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50"
                        >
                            {deleting === workoutId ? 'Deleting...' : 'Delete'}
                        </button>
                        <button
                            onClick={closeToast}
                            className={`flex-1 py-2 px-4 rounded-md transition-colors text-sm font-medium ${darkMode
                                    ? 'bg-gray-600 text-gray-200 hover:bg-gray-500'
                                    : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                                }`}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            ),
            {
                position: "top-center",
                autoClose: false, // Don't auto-close
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: false,
                draggable: false,
                closeButton: false, // Remove default close button
                className: "custom-confirm-toast",
                bodyClassName: "p-0", // Remove default padding
            }
        );
    };

    if (loading) {
        return (
            <div className="max-w-4xl mx-auto px-4 py-8">
                <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-6`}>Workout History</h2>
                <div className={`text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading...</div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto px-4 py-8">
            <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-6`}>Workout History</h2>
            {workouts.length > 0 ? (
                <div className="space-y-4">
                    {workouts.map((workout) => (
                        <div key={workout.id} className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6`}>
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                        {new Date(workout.date).toLocaleDateString()}
                                    </h3>
                                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {workout.totalExercises} exercises • {workout.points} points
                                    </p>
                                </div>
                                <button
                                    onClick={() => confirmDeleteWorkout(workout.id, workout.date)}
                                    disabled={deleting === workout.id}
                                    className={`text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed p-2 rounded-md ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-red-50'
                                        } transition-colors`}
                                    title="Delete workout"
                                >
                                    {deleting === workout.id ? (
                                        <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                                    ) : (
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                            <div className="space-y-2">
                                {workout.exercises.map((exercise, index) => (
                                    <div key={index} className="flex justify-between items-center text-sm">
                                        <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{exercise.name}</span>
                                        <span className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                            {exercise.sets} sets × {exercise.reps} reps
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <p>Your workout history will appear here.</p>
                </div>
            )}
        </div>
    );
};

export default History;